import React, {useCallback, useEffect, useRef, useState, useMemo, memo} from 'react';
import {
    Grid, Button, Typography, TextField, Alert, CircularProgress
} from "@mui/material";
import {Link as LinkUI, useNavigate, useLocation} from "react-router-dom";
import { useDispatch } from "react-redux";
import { handleLogin } from "../index";
import { styled } from "@mui/material/styles";
import LoginIcon from '@mui/icons-material/Login';
import DomainIcon from '@mui/icons-material/Domain';
import { useSettings } from "../../../../Hooks/useSettings";

const Link = styled(LinkUI)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '1.2rem',
    '&:hover': {
        cursor: 'pointer',
        color: theme.palette.primary.dark,
    },
}));

const validate = (data, setErrors, minPasswordLength = 8, maxPasswordLength = 128) => {
    const newErrors = {};
    if (!data.username.trim()) {
        newErrors.username = "Username is required.";
    } else {
        const usernamePattern = /^[a-zA-Z0-9_.@]+$/;
        if (!usernamePattern.test(data.username)) {
            newErrors.username = "Username contains invalid characters.";
        }
    }
    if (!data.password.trim()) {
        newErrors.password = "Password is required.";
    } else {
        const passwordPattern = new RegExp(`^[\\x21-\\x7E]{${minPasswordLength},${maxPasswordLength}}$`);
        if (!passwordPattern.test(data.password)) {
            newErrors.password = `Password must be between ${minPasswordLength} and ${maxPasswordLength} characters and contain only valid characters.`;
        }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};


export const Login = memo(() => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const userRef = useRef();
    const { getSettingValue } = useSettings();

    const minPasswordLength = useMemo(() =>
        getSettingValue("core.auth.password.min-length", 1),
        [getSettingValue]
    );
    const maxPasswordLength = useMemo(() =>
        getSettingValue("core.auth.password.max-length", 128),
        [getSettingValue]
    );

    const [formState, setFormState] = useState({
        data: { username: '', password: '' },
        errors: {},
        message: "",
        status: "info",
        isLoading: false,
        disabled: false,
        isUnverifiedUser: false,
        rememberMe: false,
        isSsoLoading: false,
        showCredentials: false
    });

    useEffect(() => {
        if (userRef.current) {
            userRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (formState?.showCredentials && userRef.current) {
            userRef.current.focus();
        }
    }, [formState?.showCredentials]);

    const ssoEnabled = getSettingValue('core.auth.sso.enabled', false);
    const credentialsEnabled = getSettingValue('core.auth.credentials.enabled', true);
    const ssoLoginUrl = getSettingValue('core.auth.sso.login_url', '/auth/sso');

    useEffect(() => {
        const method = new URLSearchParams(location.search).get('method');
        if (method === 'credentials') {
            setFormState(prev => ({...prev, showCredentials: true}));
        } else if (!ssoEnabled) {
            // If SSO is disabled, always show credentials form
            setFormState(prev => ({...prev, showCredentials: true}));
        }
    }, [location.search, ssoEnabled]);

    const handleSsoStart = useCallback(() => {
        if (!ssoEnabled) return;
        const redirectTo = new URLSearchParams(location.search).get('redirectTo') || '/dashboard';
        setFormState(prev => ({ ...prev, isSsoLoading: true }));
        const target = `${ssoLoginUrl}?redirectTo=${encodeURIComponent(redirectTo)}`;
        window.location.assign(target);
    }, [location.search, ssoEnabled, ssoLoginUrl]);


    const handleSubmit = useCallback(async () => {
        if (!validate(formState.data, (errors) => {
            setFormState(prev => ({ ...prev, errors }));
        }, minPasswordLength, maxPasswordLength)) {
            return;
        }

        setFormState(prev => ({ ...prev, isLoading: true }));

        await handleLogin({
            dispatch,
            data: {
                username: formState.data.username,
                password: formState.data.password,
                rememberMe: formState.rememberMe
            }
        }).then((res) => {
            setFormState(prev => ({
                ...prev,
                isLoading: false,
                errors: res.errors,
                message: res.message,
                status: res.status,
                disabled: res.statusCode === 429,
                isUnverifiedUser: res.statusCode === 423,
                data: { ...prev.data, password: "" }
            }));

            if (res.statusCode === 423) {
                setFormState(prev => ({
                    ...prev,
                    message: (
                        <>
                            {res.message}{" "}
                            <Typography component={Link} to={`/auth/forgot-password`} style={{ textDecoration: "underline" }}>
                                Set up password
                            </Typography>
                        </>
                    )
                }));
            }

            if (res.success) {
                navigate('/dashboard');
            }
        }).finally(() => {
            setFormState(prev => ({ ...prev, isLoading: false }));
        });
    }, [formState.data, formState.rememberMe, minPasswordLength, maxPasswordLength, dispatch, navigate]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    }, [handleSubmit]);

    const handleChange = useCallback((e) => {
        const { id, value } = e.target;
        setFormState(prev => ({
            ...prev,
            errors: { ...prev.errors, [id]: null },
            data: { ...prev.data, [id]: value }
        }));
    }, []);

    const handleBlur = useCallback((e) => {
        const { id, value } = e.target;
        const trimmedValue = value.trim();
        if (trimmedValue !== value) {
            setFormState(prev => ({
                ...prev,
                data: { ...prev.data, [id]: trimmedValue }
            }));
        }
    }, []);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography align={"center"} mb={1} fontWeight={800} className={"title"} variant={"h1"}>Sign in</Typography>
                <Typography align={"center"} mb={3} color={'text.secondary'} variant={'body2'}>Use your organization account to continue</Typography>
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={2}>
                        {ssoEnabled && (
                            <Grid item xs={12}>
                                <Button
                                    fullWidth={true}
                                    color="primary"
                                    variant={formState.showCredentials ? 'outlined' : 'contained'}
                                    disableElevation={true}
                                    onClick={handleSsoStart}
                                    size="large"
                                    disabled={formState.isSsoLoading}
                                    startIcon={<DomainIcon />}
                                    sx={{ height: 50, fontSize: 16, textTransform:'none', borderRadius: 2 }}
                                >
                                    {formState.isSsoLoading ? 'Redirecting…' : 'Continue with SSO'}
                                </Button>
                                {credentialsEnabled && (
                                    <Typography align={'center'} variant={'body2'} sx={{ mt: 1 }}>
                                        or {' '}
                                        <Button variant={'text'} size={'small'} onClick={() => setFormState(prev => ({...prev, showCredentials: !prev.showCredentials}))}>
                                            {formState.showCredentials ? 'Hide credentials' : 'Sign in with credentials'}
                                        </Button>
                                    </Typography>
                                )}
                            </Grid>
                        )}

                        {formState.message && (
                            <Grid item xs={12}>
                                <Alert severity={formState.status}>{formState.message}</Alert>
                            </Grid>
                        )}
                        {(formState.showCredentials || !ssoEnabled) && (
                        <Grid item xs={12}>
                            <TextField
                                type="text"
                                id="username"
                                ref={userRef}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                                value={formState.data?.username}
                                required
                                label="Username"
                                inputProps={{
                                    form: {
                                        autocomplete: 'off',
                                    },
                                    style: { fontSize: 20 },
                                }}
                                fullWidth
                                error={!!formState.errors?.username}
                                helperText={formState.errors?.username}
                            />
                        </Grid>)}
                        {(formState.showCredentials || !ssoEnabled) && (
                        <Grid item xs={12}>
                            <TextField
                                type="password"
                                id="password"
                                onChange={handleChange}
                                value={formState.data?.password}
                                onKeyDown={handleKeyDown}
                                required
                                label="Password"
                                inputProps={{
                                    form: {
                                        autocomplete: 'off',
                                    },
                                    style: { fontSize: 20, letterSpacing: '3px' },
                                }}
                                fullWidth
                                error={!!formState.errors?.password}
                                helperText={formState.errors?.password}
                            />
                        </Grid>)}
                        {(formState.showCredentials || !ssoEnabled) && (
                        <Grid item xs={12} container justifyContent="space-between" alignItems="center">
                            {/* FormControlLabel is used to properly link the Checkbox and its label */}
                            {/* <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formState.rememberMe}
                                        onChange={(e) => setFormState(prev => ({ ...prev, rememberMe: e.target.checked }))}
                                        color="primary"
                                    />
                                }
                                label="Remember Me"
                            /> */}
                            <Link to="/auth/forgot-password">Forgot Password?</Link>
                        </Grid>)}
                        {(formState.showCredentials || !ssoEnabled) && (
                        <Grid item xs={12}>
                            <Button
                                fullWidth={true}
                                disabled={formState.disabled || formState.isLoading}
                                color="info"
                                variant="contained"
                                disableElevation={true}
                                onClick={handleSubmit}
                                size="large"
                                startIcon={formState.isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                                sx={{ height: 50, fontSize: 16, textTransform:'none', borderRadius: 2 }}
                            >
                                {formState.isLoading ? 'Signing in…' : 'Sign In'}
                            </Button>
                        </Grid>)}
                    </Grid>
            </Grid>
        </Grid>
    );
});